import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Loader2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { PageTitle } from "../components/site/PageTitle";
import { DesignStage } from "../components/designer/DesignStage";
import { ArtPicker } from "../components/designer/ArtPicker";
import { TextTool } from "../components/designer/TextTool";
import { LayerControls } from "../components/designer/LayerControls";
import { ColorSwatches, DEFAULT_FONTS, ProductPicker, fontsHref } from "../components/designer/Pickers";
import { useSite } from "../context/SiteContext";
import { useCart } from "../context/CartContext";
import { api, errMsg } from "../lib/api";
import { mockupSrc, renderPreview } from "../lib/mockup";

const EMPTY = { art: null, texts: [] };
const hasLayers = (l) => l && (l.art || l.texts.length);

function useFonts(fonts) {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = fontsHref(fonts);
    document.head.appendChild(link);
    return () => link.remove();
  }, [fonts]);
}

export default function Designer() {
  const { products = [], settings: s = {} } = useSite();
  const fonts = useMemo(() => (s.design_fonts?.length ? s.design_fonts : DEFAULT_FONTS), [s.design_fonts]);
  useFonts(fonts);
  const { add } = useCart();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [colorIdx, setColorIdx] = useState(0);
  const [side, setSide] = useState("front");
  const [designs, setDesigns] = useState({});
  const [selected, setSelected] = useState(null);
  const [f, setF] = useState({ quantity: 1, sizes: "", notes: "", style_number: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!product && products.length) setProduct(products[0]); }, [products, product]);
  const sideObj = useMemo(() => product?.sides.find((x) => x.key === side) || product?.sides[0], [product, side]);
  if (!product || !sideObj) return <div className="py-32 text-center text-slate-400">Loading products...</div>;

  const color = product.colors[colorIdx] || product.colors[0];
  const cur = designs[sideObj.key] || EMPTY;
  const area = sideObj.area;
  const center = { cx: (area.x + area.w / 2) / 100, cy: (area.y + area.h / 2) / 100 };
  const patchSide = (fn) => setDesigns((d) => ({ ...d, [sideObj.key]: fn(d[sideObj.key] || EMPTY) }));
  const patchLayer = (id, p) => patchSide((l) => (id === "art" ? { ...l, art: { ...l.art, ...p } } : { ...l, texts: l.texts.map((t) => (t.id === id ? { ...t, ...p } : t)) }));
  const removeLayer = (id) => { patchSide((l) => (id === "art" ? { ...l, art: null } : { ...l, texts: l.texts.filter((t) => t.id !== id) })); setSelected(null); };
  const addText = (t) => {
    const id = `t${Date.now()}`;
    patchSide((l) => ({ ...l, texts: [...l.texts, { ...t, id, size: 0.07, rot: 0, cx: center.cx, cy: l.art ? Math.min(0.95, center.cy + area.h / 200 + 0.03) : center.cy }] }));
    setSelected(id);
  };
  const selLayer = selected === "art" ? cur.art : cur.texts.find((t) => t.id === selected);
  const pickProduct = (p) => { setProduct(p); setColorIdx(0); setSide(p.sides[0]?.key || "front"); setDesigns({}); setSelected(null); };

  const addToCart = async () => {
    const used = product.sides.filter((x) => hasLayers(designs[x.key]));
    if (!used.length) return toast.error("Upload a logo or add text first");
    setBusy(true);
    try {
      const refs = [];
      for (const x of used) {
        const l = designs[x.key];
        const blob = await renderPreview(mockupSrc(product, color, x.key), l);
        const fd = new FormData();
        fd.append("file", blob, `${x.key}-preview.png`);
        const { data } = await api.post("/uploads/preview", fd);
        refs.push({ side: x.label, preview_path: data.path, preview_url: data.url, art_path: l.art?.path || "", texts: l.texts.map(({ text, font, color: c }) => ({ text, font, color: c })) });
      }
      add({ product_id: product.id, item_type: product.name, colors: color.name, placement: used.map((x) => x.label).join(" + "), style_number: f.style_number,
        quantity: Math.max(1, parseInt(f.quantity, 10) || 1), sizes: f.sizes, notes: f.notes, designs: refs });
      toast.success(`${product.name} design added to cart`);
      nav("/checkout");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="pb-20">
      <PageTitle badge="Design Studio" title="Preview Your Design" subtitle="Pick an item and color, upload your logo, add a name or number, then drag, resize and rotate to see how it looks." testId="designer-title" />
      <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-[300px_1fr_330px]">
        <aside className="panel h-fit space-y-5 p-5">
          <h2 className="red-title text-xl">1. Choose item</h2>
          <ProductPicker products={products} value={product} onPick={pickProduct} />
          {product.price_note && <p className="text-xs text-slate-400" data-testid="design-price-note">{product.price_note}</p>}
          <h2 className="red-title text-xl">2. Color: <span className="text-white">{color.name}</span></h2>
          <ColorSwatches colors={product.colors} value={colorIdx} onPick={setColorIdx} />
          <div>
            <label className="label">Blank style / item # (optional)</label>
            <input className="field" value={f.style_number} onChange={(e) => setF({ ...f, style_number: e.target.value })} placeholder="e.g. Gildan 5000" data-testid="design-style-number-input" />
            <a href="/blank-apparel" target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[#60A5FA] hover:underline" data-testid="design-catalog-link">Browse our blank apparel catalogs <ExternalLink className="h-3 w-3" /></a>
          </div>
        </aside>
        <section className="space-y-3">
          {product.sides.length > 1 && (
            <div className="flex gap-2">
              {product.sides.map((x) => (
                <button key={x.key} onClick={() => { setSide(x.key); setSelected(null); }} className={`rounded-full px-5 py-2 font-display font-bold uppercase ${x.key === sideObj.key ? "bg-[#E5202A] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`design-side-${x.key}`}>
                  {x.label}{hasLayers(designs[x.key]) ? " ✓" : ""}
                </button>
              ))}
            </div>
          )}
          <DesignStage mockup={mockupSrc(product, color, sideObj.key)} area={area} layers={cur} selected={selected} onSelect={setSelected} onMove={patchLayer} />
        </section>
        <aside className="panel h-fit space-y-4 p-5">
          <h2 className="red-title text-xl">3. Logo & text</h2>
          <ArtPicker side={sideObj.label} area={area} hasArt={!!cur.art} onSet={(art) => { patchSide((l) => ({ ...l, art })); setSelected("art"); }} />
          <TextTool fonts={fonts} onAdd={addText} />
          {selLayer && <LayerControls layer={selLayer} isText={selected !== "art"} fonts={fonts} area={area} onChange={(p) => patchLayer(selected, p)} onRemove={() => removeLayer(selected)} />}
          <h2 className="red-title text-xl">4. Details</h2>
          <div className="grid grid-cols-[90px_1fr] gap-3">
            <div><label className="label">Qty</label><input className="field" type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="design-quantity-input" /></div>
            <div><label className="label">Sizes</label><input className="field" value={f.sizes} onChange={(e) => setF({ ...f, sizes: e.target.value })} placeholder="e.g. 2 M, 3 L" data-testid="design-sizes-input" /></div>
          </div>
          <div><label className="label">Notes</label><textarea className="field min-h-[60px]" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="design-notes-input" /></div>
          <button onClick={addToCart} disabled={busy} className="btn-red w-full" data-testid="design-add-to-cart-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />} Add to Cart
          </button>
          <p className="text-xs text-slate-500">Preview is a mockup. We'll confirm final placement, sizing and pricing before sending your invoice.</p>
        </aside>
      </div>
    </div>
  );
}
